import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, User2, Check, ChevronsUpDown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatApiError } from "@/lib/errors";

function initials(fullName = "") {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function humanRole(role) {
  if (!role) return "";
  return role.split("_").map((s) => s[0].toUpperCase() + s.slice(1)).join(" ");
}

function TenantSwitcher() {
  const { memberships, activeTenant, switchTenant } = useAuth();
  if (!memberships || memberships.length < 2) return null;

  const handle = async (m) => {
    if (m.tenant_id === activeTenant?.id) return;
    try {
      await switchTenant(m.tenant_id);
      toast.success(`Switched to ${m.tenant_name}`);
      // Reload so all tenant-scoped fetches reflect the new active tenant.
      setTimeout(() => window.location.reload(), 250);
    } catch (e) {
      toast.error(formatApiError(e) || "Switch failed");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="tenant-switcher"
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1.5 pr-3 text-sm shadow-sm outline-none transition-colors hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
      >
        <span
          className="h-3 w-3 rounded-full ring-2 ring-offset-1 ring-white"
          style={{ background: activeTenant?.branding?.primary_color || "#0f172a" }}
        />
        <span className="hidden max-w-[10rem] truncate text-sm font-semibold text-slate-800 sm:inline">
          {activeTenant?.name || "Select tenant"}
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-400">
          Switch tenant
        </DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.tenant_id}
            onClick={() => handle(m)}
            data-testid={`tenant-switch-${m.tenant_slug}`}
            className="cursor-pointer items-center gap-2"
          >
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: m.primary_color || "#0f172a" }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-800">{m.tenant_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                {m.is_platform ? "Platform" : humanRole(m.role)}
              </div>
            </div>
            {m.tenant_id === activeTenant?.id && <Check className="h-4 w-4 text-[var(--brand-primary)]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function TopBar({ title, onOpenSidebar }) {
  const { user, activeTenant, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onPlatform = pathname.startsWith("/platform");

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  const logo = onPlatform ? null : activeTenant?.branding?.logo_url;
  const tenantName = onPlatform
    ? "BlackScott Holdings"
    : (activeTenant?.name || "BlackScott Hub");
  const headerBg = onPlatform ? "#0f172a" : undefined;
  const swatchBg = onPlatform ? "#0f172a" : (activeTenant?.branding?.primary_color || "#0f172a");

  return (
    <header
      data-testid="topbar"
      data-platform={onPlatform ? "1" : undefined}
      className={
        onPlatform
          ? "sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-800 bg-slate-900 px-4 text-slate-100 sm:px-6"
          : "sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6"
      }
    >
      <button
        type="button"
        onClick={onOpenSidebar}
        className={
          onPlatform
            ? "rounded-md p-2 text-slate-300 hover:bg-slate-800 lg:hidden"
            : "rounded-md p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
        }
        aria-label="Open sidebar"
        data-testid="topbar-menu"
      >
        <Menu className="h-5 w-5" />
      </button>
      {logo ? (
        <img
          src={logo}
          alt={`${tenantName} logo`}
          data-testid="topbar-tenant-logo"
          className="h-7 max-w-[9rem] object-contain object-left"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <span
          data-testid="topbar-tenant-swatch"
          className={
            onPlatform
              ? "flex h-7 items-center gap-2 rounded-md border border-amber-400/40 px-2 text-xs font-semibold text-amber-300"
              : "flex h-7 items-center gap-2 rounded-md px-2 text-xs font-semibold text-white"
          }
          style={onPlatform ? { background: "transparent" } : { background: swatchBg }}
        >
          {onPlatform && <ShieldCheck className="h-3.5 w-3.5" />}
          {tenantName}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <h1
          data-testid="topbar-title"
          className={
            onPlatform
              ? "truncate font-display text-base font-semibold tracking-tight text-slate-100 sm:text-lg"
              : "truncate font-display text-base font-semibold tracking-tight text-slate-900 sm:text-lg"
          }
        >
          {title}
        </h1>
      </div>

      <TenantSwitcher />
      <NotificationBell />

      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid="topbar-user-menu"
          className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 text-sm shadow-sm outline-none transition-colors hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
        >
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ background: "var(--brand-primary, #0f172a)" }}
          >
            {initials(user?.full_name)}
          </span>
          <span className="hidden text-sm font-medium text-slate-700 sm:inline">
            {user?.full_name || user?.email || "Account"}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span data-testid="usermenu-name" className="text-sm font-semibold text-slate-900">
                {user?.full_name}
              </span>
              <span data-testid="usermenu-email" className="truncate text-xs font-normal text-slate-500">
                {user?.email}
              </span>
              <div className="mt-1 flex flex-wrap gap-1">
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white"
                  style={{ background: "var(--brand-primary, #0f172a)" }}
                >
                  {humanRole(user?.role)}
                </span>
                {user?.is_platform_admin && (
                  <span
                    data-testid="platform-admin-badge"
                    className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    Platform
                  </span>
                )}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled className="gap-2 text-slate-500">
            <User2 className="h-4 w-4" />
            Profile (soon)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            data-testid="logout-button"
            className="gap-2 text-cargodash-alert-600 focus:bg-cargodash-alert-600/10 focus:text-cargodash-alert-600"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
